import {
  OrgUsers,
  SupportedLanguages,
  UserMessageI18n,
  Permissions,
  Organizations,
  ReferenceLists,
  SuperPermissions,
} from 'utils/gloo-types';
import emojiRegex from 'emoji-regex';
import { isString, uniqArr } from './common';
import { Configs } from './constants';

export const emailRegex =
  /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

export function convertToSelectOptions(options: (string | number)[]) {
  return options.filter(Boolean).map((o: number | string) => ({ value: o.toString(), label: o.toString() }));
}

export function convertReferenceListToSelectOptions(options: Partial<ReferenceLists>[]) {
  return options
    .filter(Boolean)
    .map((o: ReferenceLists) => ({ value: o.value?.toString(), label: o.title?.toString() }));
}

export function timeNow() {
  return new Date().valueOf();
}

export function removeDuplicateSpaces(text: string) {
  return text.replace(/\s\s+/g, ' ').trim();
}
export function removeNonChars(text: string) {
  // apostrophe should not be replaced with space
  return text.replace(/[']/g, '').replace(/[^a-z0-9\s]/g, ' ');
}

export function isUserThryveAdmin(user?: OrgUsers | null) {
  if (user?.extra.isThryveAdmin) {
    return true;
  }
  return false;
}

function getOrgUserSpecialPermissionsForAnyOrg(user: OrgUsers): (Permissions | SuperPermissions)[] {
  const accessLimiters = user?.extra?.orgAccessLimiters || [];
  if (accessLimiters) {
    const permissionsForOrg: (Permissions | SuperPermissions)[] = [];
    accessLimiters.forEach(({ permissions }) => {
      permissionsForOrg.push(...(permissions || []));
    });
    if (permissionsForOrg.length > 0) {
      return uniqArr(permissionsForOrg);
    }
  }
  return [];
}

export function userHasSpecialPermissionForAnyOrg(user: OrgUsers, permissions: SuperPermissions[]) {
  if (isUserThryveAdmin(user)) {
    return true;
  }
  const userPermissions = getOrgUserSpecialPermissionsForAnyOrg(user);
  return permissions.some((p) => userPermissions.includes(p));
}

export function getI18nResponse(response?: UserMessageI18n | string | null, userLanguage?: SupportedLanguages) {
  if (!response) {
    return '';
  }
  if (isString(response)) {
    return response;
  }
  return response[userLanguage] || response[response.default] || '';
}

export function getUserNameOrg(user?: Partial<OrgUsers>, anonymousText = '(No Name)') {
  return !user || (!user.firstName && !user.lastName && !user.name)
    ? anonymousText
    : user.name
      ? user.name
      : `${user?.firstName || ''}${user?.lastName ? ` ${user?.lastName}` : ''}`;
}

export const getOrgUserInitials = (orgUser?: Partial<OrgUsers>) => {
  if (!orgUser) return '';
  if (orgUser.firstName && orgUser.lastName) {
    return orgUser.firstName[0] + orgUser.lastName[0];
  }
  const names = orgUser.name.split(' ');

  return `${names[0].substring(0, 1).toUpperCase()}${(names[1] || '').substring(0, 1).toUpperCase()}`;
};

export function getMessageCredits(message: UserMessageI18n | string, media?: string[]): number {
  if (media && media.length) {
    return Configs.Sms.MediaMessageCost;
  }
  const messageStr = isString(message) ? message : getI18nResponse(message);
  const regex = emojiRegex();
  const hasEmoji = regex.test(messageStr);
  // Number of credits is variable: https://sakari.io/sms-length-calculator/
  if (hasEmoji) {
    if (messageStr.length > Configs.Sms.EmojiSegmentLimit) {
      return Math.ceil(messageStr.length / Configs.Sms.EmojiSegmentLimitWhenMulti);
    }
    return Math.ceil(messageStr.length / Configs.Sms.EmojiSegmentLimit);
  }
  if (messageStr.length > Configs.Sms.CharacterSegmentLimit) {
    return Math.ceil(messageStr.length / Configs.Sms.CharacterSegmentLimitWhenMulti);
  }
  return Math.ceil(messageStr.length / Configs.Sms.CharacterSegmentLimit);
}

export function pluralWord(count: number, text: string, ending = 's') {
  if (count === 1) return text;
  return text + ending;
}
export function pluralStr(count: number, singleText: string, multiText: string) {
  if (count === 1) return singleText;
  return multiText;
}

export const canOrgDoMMS = (org: Organizations) => ['bandwidth', 'infobip'].includes(org?.config?.provider);
